import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, FileText, IndianRupee, Receipt } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import BillingClientPage from "@/pages/BillingClientPage";
import EmptyState from "@/components/EmptyState";

export default function BillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/billing/summary").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    }
  }, [user]);

  if (user?.role === "client") {
    return <BillingClientPage embeddedClientId={user.client_id} />;
  }

  const totals = rows ? {
    billed: rows.reduce((s, r) => s + r.billed_amount, 0),
    unbilled: rows.reduce((s, r) => s + r.unbilled_amount, 0),
    paid: rows.reduce((s, r) => s + r.paid_amount, 0),
    bills: rows.reduce((s, r) => s + r.bills_count, 0),
  } : null;

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="billing-title">Billing</h1>
        <p className="text-sm text-slate-500 mt-1">Upload bills per client and mark orders as billed.</p>
      </div>

      {!rows ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-stats">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Billed Amount</span>
              <span className="h-8 w-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center"><IndianRupee className="h-4 w-4" /></span>
            </div>
            <div className="mt-1.5 text-2xl font-bold text-slate-900 tnum" data-testid="billing-total-billed">{inr(totals.billed)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Unbilled Amount</span>
              <span className="h-8 w-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><IndianRupee className="h-4 w-4" /></span>
            </div>
            <div className="mt-1.5 text-2xl font-bold text-slate-900 tnum" data-testid="billing-total-unbilled">{inr(totals.unbilled)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Collected (Paid)</span>
              <span className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><IndianRupee className="h-4 w-4" /></span>
            </div>
            <div className="mt-1.5 text-2xl font-bold text-slate-900 tnum" data-testid="billing-total-paid">{inr(totals.paid)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Bills Uploaded</span>
              <span className="h-8 w-8 rounded-lg bg-[#1B3A6B]/10 text-[#1B3A6B] flex items-center justify-center"><Receipt className="h-4 w-4" /></span>
            </div>
            <div className="mt-1.5 text-2xl font-bold text-slate-900 tnum" data-testid="billing-total-bills">{totals.bills}</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
        {!rows ? (
          <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-sm animate-pulse" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No clients yet" testId="billing-empty" />
        ) : (
          <div className="divide-y divide-slate-100" data-testid="billing-clients-list">
            {rows.map((r) => (
              <button
                key={r.client_id}
                onClick={() => navigate(`/billing/${r.client_id}`)}
                data-testid={`billing-client-${r.client_id}`}
                className="w-full flex items-center gap-4 px-4 md:px-5 py-4 hover:bg-blue-50/40 transition-colors duration-100 text-left"
              >
                <div className="h-10 w-10 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.company_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.billed_count}/{r.orders} billed · {r.paid_count} paid · {r.bills_count} bill{r.bills_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-green-700 tnum">{inr(r.billed_amount)}</div>
                  <div className="text-xs text-orange-600 tnum">{inr(r.unbilled_amount)} unbilled</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
